import React, { useMemo, useState, useEffect } from "react";

const avi = (n) =>
  `https://ui-avatars.com/api/?name=${encodeURIComponent(n || "C")}&background=fff000&color=000000&size=160&bold=true`;
const PHONE = /^(\+?254|0)[17]\d{8}$/;

const positive = (v) => {
  const x = Number(v);
  return Number.isFinite(x) && x > 0 ? x : 0;
};
// same rule as the tracker and invoices: what was actually charged
const paidAmount = (j) => positive(j.amount_paid) || positive(j.amount_due) || positive(j.budget);

const CSS = `
.prf { --ink:#0c0c0b; --muted:#6b6b66; --line:#e6e6e1; --fill:#f3f3ef; --y:#fff000;
  max-width: 760px; margin: 0 auto; color: var(--ink); font-family: inherit; font-size: 14px; line-height: 1.5; }
.prf *, .prf *::before, .prf *::after { box-sizing: border-box; }
.prf :focus-visible { outline: 2px solid var(--ink); outline-offset: 2px; }

.prf-top { display: flex; align-items: center; gap: 18px; padding: 22px; margin-bottom: 16px; background: #fff; border: 1px solid var(--line); border-radius: 14px; }
.prf-top img { width: 72px; height: 72px; flex: 0 0 72px; border-radius: 50%; object-fit: cover; border: 1px solid var(--line); background: var(--fill); }
.prf-top h1 { margin: 0; font-size: clamp(20px, 3vw, 24px); font-weight: 700; letter-spacing: -.02em; line-height: 1.2; overflow-wrap: anywhere; }
.prf-top p { margin: 4px 0 0; color: var(--muted); overflow-wrap: anywhere; }

.prf-strip { display: grid; grid-template-columns: repeat(3, 1fr); margin-bottom: 16px; background: #fff; border: 1px solid var(--line); border-radius: 14px; }
.prf-stat { min-width: 0; padding: 16px 22px; }
.prf-stat + .prf-stat { border-left: 1px solid var(--line); }
.prf-stat span { display: block; color: var(--muted); font-size: 13px; }
.prf-stat b { display: block; margin-top: 2px; font-size: 22px; font-weight: 700; letter-spacing: -.02em; font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }

.prf-card { background: #fff; border: 1px solid var(--line); border-radius: 14px; }
.prf-card-hd { padding: 16px 22px; border-bottom: 1px solid var(--line); }
.prf-card-hd h2 { margin: 0; font-size: 15px; font-weight: 600; }
.prf-card-hd p { margin: 2px 0 0; color: var(--muted); font-size: 13px; }
.prf-bd { padding: 22px; }
.prf-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; }
.prf-f { display: flex; flex-direction: column; gap: 8px; min-width: 0; }
.prf-f.full { grid-column: 1 / -1; }
.prf-f label { font-size: 14px; font-weight: 600; }
.prf-f input { width: 100%; height: 50px; padding: 0 14px; border: 2px solid transparent; border-radius: 8px; background: var(--fill); color: var(--ink); font: inherit; font-size: 16px; }
.prf-f input:focus { outline: none; border-color: var(--ink); background: #fff; }
.prf-f input.bad { border-color: #a8201a; background: #fff; }
.prf-f small { color: #a8201a; font-size: 13px; font-weight: 500; }
.prf-hint { color: var(--muted); font-size: 13px; }

.prf-ft { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; padding: 16px 22px; border-top: 1px solid var(--line); }
.prf-btn { display: inline-flex; align-items: center; justify-content: center; gap: 8px; min-height: 44px; padding: 0 22px; border: 0; border-radius: 8px; background: var(--y); color: #000; font: inherit; font-size: 15px; font-weight: 700; cursor: pointer; }
.prf-btn:hover:not(:disabled) { background: #ffe600; }
.prf-btn:disabled { background: var(--fill); color: #9a9a94; cursor: not-allowed; }
.prf-ok { display: inline-flex; align-items: center; gap: 6px; color: #17602f; font-weight: 600; }
.prf-err { color: #a8201a; font-weight: 500; }

@media (max-width: 700px) {
  .prf-strip { grid-template-columns: 1fr; }
  .prf-stat + .prf-stat { border-left: 0; border-top: 1px solid var(--line); }
  .prf-grid { grid-template-columns: 1fr; }
  .prf-btn { width: 100%; }
}
`;

// Props: client, jobs, onSave(updates) -> Promise (throws Error with a message on failure)
export default function ProfileTab({ client = {}, jobs = [], onSave }) {
  const initial = useMemo(() => ({
    full_name: client.full_name || "",
    email: client.email || "",
    phone: client.phone || client.phone_number || "",
  }), [client]);

  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState({ ok: false, text: "" });
  useEffect(() => setF(initial), [initial]);

  const stats = useMemo(() => ({
    total: jobs.length,
    done: jobs.filter((j) => /complet|paid|closed/i.test(j.status || "")).length,
    spent: jobs.filter((j) => /paid/i.test(j.status || "")).reduce((s, j) => s + paidAmount(j), 0),
  }), [jobs]);

  const errs = {
    full_name: f.full_name.trim().length < 2 ? "Enter your full name." : "",
    email: f.email && !/^\S+@\S+\.\S+$/.test(f.email) ? "Enter a valid email address." : "",
    phone: f.phone && !PHONE.test(f.phone.replace(/\s/g, "")) ? "Use a Kenyan number, e.g. 0712345678." : "",
  };
  const valid = !Object.values(errs).some(Boolean);
  const dirty = Object.keys(initial).some((k) => (f[k] || "").trim() !== (initial[k] || "").trim());
  const set = (k) => (e) => { setF((p) => ({ ...p, [k]: e.target.value })); setMsg({ ok: false, text: "" }); };

  const save = async (e) => {
    e.preventDefault();
    if (!valid || !dirty || !onSave) return;
    setBusy(true);
    setMsg({ ok: false, text: "" });
    try {
      await onSave({ full_name: f.full_name.trim(), email: f.email.trim(), phone: f.phone.replace(/\s/g, "") });
      setMsg({ ok: true, text: "Saved" });
    } catch (err) {
      setMsg({ ok: false, text: err?.message || "Could not save changes." });
    }
    setBusy(false);
  };

  const field = (k, label, type = "text", full = false, hint = "") => (
    <div className={`prf-f${full ? " full" : ""}`}>
      <label htmlFor={`prf-${k}`}>{label}</label>
      <input
        id={`prf-${k}`}
        type={type}
        value={f[k]}
        onChange={set(k)}
        className={errs[k] ? "bad" : ""}
        aria-invalid={!!errs[k]}
        autoComplete={k === "full_name" ? "name" : k === "email" ? "email" : "tel"}
      />
      {errs[k] ? <small role="alert">{errs[k]}</small> : hint ? <span className="prf-hint">{hint}</span> : null}
    </div>
  );

  return (
    <div className="prf">
      <style>{CSS}</style>

      <div className="prf-top">
        <img src={avi(client.full_name)} alt="" />
        <div>
          <h1>{client.full_name || "Your profile"}</h1>
          <p>{client.email || "Client account"}</p>
        </div>
      </div>

      <div className="prf-strip">
        <div className="prf-stat"><span>Total requests</span><b>{stats.total}</b></div>
        <div className="prf-stat"><span>Completed</span><b>{stats.done}</b></div>
        <div className="prf-stat"><span>Total spent</span><b>KSh {stats.spent.toLocaleString("en-KE")}</b></div>
      </div>

      <form className="prf-card" onSubmit={save} noValidate>
        <div className="prf-card-hd">
          <h2>Your details</h2>
          <p>Craftsmen see your name. Your phone number is used for M-Pesa payments.</p>
        </div>

        <div className="prf-bd">
          <div className="prf-grid">
            {field("full_name", "Full name", "text", true)}
            {field("email", "Email", "email")}
            {field("phone", "Phone (M-Pesa)", "tel", false, "e.g. 0712345678")}
          </div>
        </div>

        <div className="prf-ft">
          <button className="prf-btn" type="submit" disabled={busy || !valid || !dirty}>
            {busy ? "Saving…" : "Save changes"}
          </button>
          {msg.text && (msg.ok
            ? <span className="prf-ok" role="status"><i className="fas fa-check" aria-hidden="true" /> {msg.text}</span>
            : <span className="prf-err" role="alert">{msg.text}</span>)}
        </div>
      </form>
    </div>
  );
}
